import React, { useState, useCallback, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import "./carriers.scss";
import { useCarriersList } from "hooks/useCarrier";
import ContentContainer from "templates/content-container/contentContainer";
import { UserUtils } from "app/data/user/userUtils";
import { PAGE_IDS } from "app/data/common/pageIds";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";
import UserState from "slices/user/UserState";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import CarrierViewDetails from "./CarriersDetails/carriersDetails";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Tag from "ui-components/molecules/tag/tag";
import CarriersAddEditDetails from "./CarriersDetails/carriersaddEditDetails";
import { TagColor } from "app/data/common/tagColor";
import { Popover } from "ui-components/popover/popover";

const columnHelper = createColumnHelper<any>();

const Carriers: React.FC<{}> = () => {
  const userState: UserState = useSelector(userSelector);
  const [carrierSearch, setCarrierSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [carrierNumber, setCarrierNumber] = useState<number | null>(null);
  const [showCreateTeam, setShowCreateTeam] = useState<boolean>(false);

  const columns = [
    columnHelper.accessor("carrierNumber", {
      id: "carrierNumber",
      header: "Carrier ID",
      size: 10,
    }),
    columnHelper.accessor("carrierName", {
      id: "carrierName",
      header: "Carrier Name",
      size: 130,
    }),
    columnHelper.accessor("city", {
      id: "city",
      header: "City, State",
      cell: (cellProps) => (
        <span>
          {[
            cellProps.getValue(),
            cellProps?.row?.original?.stateProvince
          ]
            .filter(Boolean)
            .join(", ")}
        </span>
      ),
      size: 80,
    }),

    columnHelper.accessor("scacCode", {
      id: "scacCode",
      header: () => (
        <>
          SCAC <Popover
            values={
              <div className="popover-value">
                SCAC (Standard Carrier Alpha Code) — a unique four-letter code assigned to carriers by NMFTA for identification in shipping and freight systems.
              </div>
            }
          />
        </>
      ),
      size: 30,
    }),

    columnHelper.accessor("mcNumber", {
      id: "mcNumber",
      header: () => (
        <>
          MC Number <Popover
            values={
              <div className="popover-value">
                MC Number — a registration number issued by the FMCSA that allows carriers to operate as a for-hire transport company in the U.S.
              </div>
            }
          />
        </>
      ),
      size: 80,
    }),

    columnHelper.accessor("phoneNumber", {
      id: "phoneNumber",
      header: "Phone",
      cell: (cellProps) =>
        cellProps.getValue() && cellProps.getValue() !== "0"
          ? cellProps.getValue().formatPhoneNumber()
          : "N/A",
      size: 50,
    }),

    columnHelper.accessor("status", {
      id: "status",
      header: "Status",
      cell: (cellProps) => (
        <Tag mods={{ color: cellProps.getValue() === "ACTIVE" ? TagColor.GREEN : TagColor.GREY, uppercase: true }}>
          {cellProps.getValue()}
        </Tag>
      ),
      size: 30,
    })
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useCarriersList(carrierSearch || undefined);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCarrierSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleScroll = useCallback(() => {
    const scrollContainer = document.querySelector(".xgs-carrier-master__orders");
    if (!scrollContainer) return;

    const bottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop <=
      scrollContainer.clientHeight + 10;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);


  const onSearchStart = useCallback(() => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setCarrierSearch("");
      return;
    }
    setCarrierSearch(trimmed);
  }, [searchInput]);

  const onSearchClear = useCallback(() => {
    setSearchInput("");
    setCarrierSearch("");
  }, []);

  return (
    <ContentContainer
      pageId={PAGE_IDS.CARRIERS}
      title="Carriers"
      mods={{ 'full-width': true }}
    >
      <div className="xgs-carrier-master">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <LabeledTextInput
              labelMode={LabelModes.column}
              className="xgs-list__controls__search__input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => (e.key === "Enter") && !isLoading && setCarrierSearch(searchInput.trim())}
              type="text"
              placeholder="Search by Carrier ID, Name, SCAC or MC Number"
            />
            <Button
              className="xgs-list__controls__button"
              theme={ButtonThemes.blue}
              disabled={isLoading}
              onClick={onSearchStart}>
              Search
            </Button>
            <Button
              className="xgs-list__controls__button"
              theme={ButtonThemes.gray}
              onClick={onSearchClear}>
              Clear Filters
            </Button>
          </div>
          <div className="xgs-list__controls__actions">
            {UserUtils.isXGSAdministrator(userState.profile) && (
              <Button
                className="xgs-carrier-master__create-carrier-button"
                theme={ButtonThemes.blue}
                onClick={() => setShowCreateTeam(true)}
              >
                + Add Carrier
              </Button>
            )}
          </div>
        </div>
        {error && (
          <XGSErrorMessage>{error}</XGSErrorMessage>
        )}
        {!error && (
          <div className="xgs-carrier-master__orders">
            <Table
              isLoading={isLoading} // shows loading while fetching
              columns={columns}
              data={data?.allItems || []}
              noResultsText="There are no records available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={handleScroll}
              responsive={true}
              sorting={true}
              minTableHeight={420}
              cursorPointer={true}
              onRowClicked={(row: any) => setCarrierNumber(row.carrierNumber)}
              autoResetSelectedRows={true}
            />
          </div>
        )}
        <CarriersAddEditDetails
          show={showCreateTeam}
          onClose={() => setShowCreateTeam(false)}
          onTeamCreated={() => setCarrierSearch("")}
        />
        <CarrierViewDetails
          carrierNumber={carrierNumber}
          show={!!carrierNumber}
          onClose={() => setCarrierNumber(null)}
        />
      </div>
    </ContentContainer >
  );
};

export default Carriers;
